import {Badge, Box, Button, Text} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import React, {useState, type FC} from 'react';
import {FaFire, FaLightbulb} from 'react-icons/fa';
import {MdDownload} from 'react-icons/md';
import {useAuth} from '../../../../core/usecases/auth/AuthContext';
import {CommonLink} from '../../../../shared/components/CommonLink';
import {NoLoginAlertModal} from '../../../../shared/components/NoLoginAlertModal';
import {MarkdownText} from './MarkdownText';

type Props = {
  requirement: {
    title: string;
    requirements: string;
    difficulty: number;
    sample: string;
    zip: {
      fileName: string;
      filePath: string;
    };
    hints: Array<{
      title: string;
      hint: string;
    }>;
    technologyTags: string[];
    problemTags: string[];
    timeRequired: number;
  };
};

export const DrillRequirementCard: FC<Props> = ({requirement}) => {
  const {status} = useAuth();
  const [visibleHint, setVisibleHint] = useState<number[]>([]);
  const [loginModalOpened, {open: loginModalOpen, close: loginModalClose}] =
    useDisclosure(false);

  return (
    <>
      <NoLoginAlertModal opened={loginModalOpened} onClose={loginModalClose} />
      <Box className="p-12 bg-white">
        <Box className="flex flex-wrap gap-8 my-8">
          {[...requirement.problemTags, ...requirement.technologyTags].map(
            (tag) => (
              <Badge
                key={tag}
                className="bg-background-2 text-black text-[10px] px-[6px] normal-case"
              >
                {tag}
              </Badge>
            ),
          )}
        </Box>
        <Box className="mb-12">
          <Text className="font-bold text-md">{requirement.title}</Text>
        </Box>
        <hr className="my-12 border-black-3" />
        <Box className="text-[14px] px-8 my-8 flex gap-x-16 items-start">
          <Text className="font-bold pb-12">難易度</Text>
          <Box className="flex items-center">
            <FaFire />
            <Text className="text-black-1">レベル{requirement.difficulty}</Text>
          </Box>
        </Box>
        <Box className="text-[14px] px-8 my-8 flex gap-x-16">
          <Text className="font-bold pb-12">参考回答時間</Text>
          <Text className="text-black-1">{requirement.timeRequired}時間</Text>
        </Box>
        <Box className="text-[14px] px-8 my-8">
          <MarkdownText>{requirement.requirements}</MarkdownText>
        </Box>
        {requirement.sample && (
          <Box className="text-[14px] p-12 my-20 bg-background rounded-[6px]">
            <Text className="font-bold pb-12">サンプル</Text>
            <MarkdownText>{requirement.sample}</MarkdownText>
          </Box>
        )}
        {requirement.zip?.fileName && (
          <Box className="text-[14px] px-8 my-8">
            <Text className="font-bold pb-12">素材</Text>
            <Box className="flex gap-16 items-center mb-32">
              <Text className="text-black-1">{requirement.zip.fileName}</Text>
              <CommonLink
                className="flex items-center gap-8 rounded-full bg-transparent text-primary border-2 border-primary py-[6px] ps-8 pe-16 font-bold"
                href={requirement.zip.filePath}
              >
                <MdDownload />
                ダウンロード
              </CommonLink>
            </Box>
          </Box>
        )}
        {requirement.hints.length > 0 && (
          <Box className="text-[14px] p-12 my-20 bg-background rounded-[6px]">
            <Text className="font-bold pb-4">ヒント</Text>
            {requirement.hints.map((hint, index) => (
              <React.Fragment key={hint.title}>
                <Box className="flex gap-16 items-center mt-16 mb-12">
                  <Text className="text-black-1">{hint.title}</Text>
                  <Button
                    className="rounded-full bg-transparent text-primary border-2 border-primary"
                    leftIcon={<FaLightbulb />}
                    onClick={() => {
                      if (status === 'unauthenticated') {
                        loginModalOpen();
                        return;
                      }

                      if (visibleHint.includes(index)) {
                        setVisibleHint(
                          visibleHint.filter(
                            (_, i) => i !== visibleHint.indexOf(index),
                          ),
                        );
                      } else {
                        setVisibleHint([...visibleHint, index]);
                      }
                    }}
                  >
                    ヒントをみる
                  </Button>
                </Box>
                {visibleHint.includes(index) && (
                  <MarkdownText>{hint.hint}</MarkdownText>
                )}
              </React.Fragment>
            ))}
          </Box>
        )}
      </Box>
    </>
  );
};
